// sales-leads-v1
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, BRANCH_KEY } from "@/lib/api";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FieldError } from "@/components/FieldError";
import { PRIORITIES, PROJECT_TYPES, SOURCES, splitIso, toIso } from "@/components/sales/leadUtils";

const blank = { title: "", contact_phone: "", contact_email: "", location: "", project_type: "", estimated_value: "", source: "", priority: "MEDIUM", owner_id: "", requirement: "", fu_date: "", fu_time: "09:00", client_id: "", branch_id: "" };

function Pick({ label, value, onChange, options, none = "— none —", testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
        <SelectTrigger className="mt-1.5" data-testid={testId}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>
          {none !== null && <SelectItem value="none">{none}</SelectItem>}
          {options.map((o) => <SelectItem key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

/** Add or edit a lead. `lead` null = add. */
export function LeadDialog({ open, onOpenChange, lead, owners = [], clients = [], onSaved }) {
  const [f, setF] = useState(blank);
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const [branches, setBranches] = useState([]);
  const needsBranch = !lead && branches.length > 1;
  useEffect(() => {
    if (!open || lead) return;
    api.get("/masters/branches").then(({ data }) => {
      const active = data.filter((b) => b.is_active);
      setBranches(active);
      let saved = ""; try { saved = localStorage.getItem(BRANCH_KEY) || ""; } catch { /* no storage */ }
      setF((x) => ({ ...x, branch_id: active.length === 1 ? active[0].id : (active.some((b) => b.id === saved) ? saved : "") }));
    }).catch(() => {});
  }, [open, lead]);
  useEffect(() => {
    if (!open) return;
    setErr({});
    if (lead) {
      const fu = splitIso(lead.next_followup_at);
      setF({ ...blank, title: lead.title || "", contact_phone: lead.contact_phone || "", contact_email: lead.contact_email || "", location: lead.location || "",
        project_type: lead.project_type || "", estimated_value: lead.estimated_value ?? "", source: lead.source || "", priority: lead.priority || "MEDIUM",
        owner_id: lead.owner_id || "", requirement: lead.requirement || "", client_id: lead.client_id || "", fu_date: fu.date, fu_time: fu.time || "09:00" });
    } else setF((x) => ({ ...blank, branch_id: x.branch_id }));
  }, [open, lead]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const inp = (k) => ({ value: f[k], onChange: (e) => set(k)(e.target.value), className: "mt-1.5" });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr({});
    const body = {
      title: f.title.trim(), contact_phone: f.contact_phone.trim() || null, contact_email: f.contact_email.trim() || null, location: f.location.trim() || null,
      project_type: f.project_type || null, estimated_value: f.estimated_value === "" ? null : Number(f.estimated_value), source: f.source || null,
      priority: f.priority, owner_id: f.owner_id || null, requirement: f.requirement.trim() || null, client_id: f.client_id || null,
      next_followup_at: toIso(f.fu_date, f.fu_time),
      ...(lead ? {} : { branch_id: f.branch_id || null }),
    };
    try {
      const { data } = lead ? await api.patch(`/pipeline/leads/${lead.id}`, body) : await api.post("/pipeline/leads", body);
      toast.success(lead ? "Lead updated" : "Lead added"); onOpenChange(false); onSaved && onSaved(data);
    } catch (ex) { setErr(getFieldErrors(ex)); toast.error(getErrorMessage(ex), { duration: 8000 }); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="lead-dialog">
        <DialogHeader><DialogTitle>{lead ? `Edit ${lead.code}` : "Add lead"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-3">
          {needsBranch && (
            <div className="col-span-2">
              <Pick label="Branch *" value={f.branch_id} onChange={set("branch_id")} options={branches.map((b) => ({ value: b.id, label: b.name }))} none="Choose the branch" testId="lead-branch" />
              <p className="mt-1 text-xs text-muted-foreground">This branch stays with the lead through the quotation, contract, project, billing and payments.</p>
            </div>
          )}
          <div className="col-span-2"><Label>Name</Label><Input required placeholder="Person or company" data-testid="lead-name" {...inp("title")} /><FieldError error={err.title} /></div>
          <div><Label>Phone</Label><Input type="tel" data-testid="lead-phone" {...inp("contact_phone")} /><FieldError error={err.contact_phone} /></div>
          <div><Label>Email</Label><Input type="email" {...inp("contact_email")} /><FieldError error={err.contact_email} /></div>
          <div><Label>Location</Label><Input placeholder="e.g. Nagercoil" {...inp("location")} /></div>
          <Pick label="Project type" value={f.project_type} onChange={set("project_type")} options={PROJECT_TYPES} testId="lead-project-type" />
          <div><Label>Budget (₹)</Label><Input type="number" min="0" step="1" data-testid="lead-budget" {...inp("estimated_value")} /><FieldError error={err.estimated_value} /></div>
          <Pick label="Source" value={f.source} onChange={set("source")} options={SOURCES} />
          <Pick label="Priority" value={f.priority} onChange={set("priority")} options={PRIORITIES.map((p) => ({ value: p, label: p[0] + p.slice(1).toLowerCase() }))} none={null} testId="lead-priority" />
          <Pick label="Owner" value={f.owner_id} onChange={set("owner_id")} options={owners.map((o) => ({ value: o.id, label: o.name }))} none="Unassigned" />
          {clients.length > 0 && <Pick label="Existing client (optional)" value={f.client_id} onChange={set("client_id")} options={clients.map((c) => ({ value: c.id, label: `${c.code} · ${c.name}` }))} />}
          <div className="col-span-2 grid grid-cols-2 gap-3">
            <div><Label>Next follow-up</Label><Input type="date" value={f.fu_date} onChange={(e) => set("fu_date")(e.target.value)} className="mt-1.5" /></div>
            <div><Label>Time</Label><Input type="time" value={f.fu_time} onChange={(e) => set("fu_time")(e.target.value)} className="mt-1.5" /></div>
          </div>
          <div className="col-span-2"><Label>Requirement</Label><Textarea rows={3} placeholder="What does the client want built?" {...inp("requirement")} /></div>
          <DialogFooter className="col-span-2"><Button type="submit" disabled={busy || (needsBranch && !f.branch_id)} data-testid="lead-save">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {lead ? "Save" : "Add lead"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
